import toast from 'react-hot-toast';

/**
 * Execute an action with undo support.
 *
 * Shows a toast with "Undo" button. The action only commits to the backend after
 * the timeout expires (so undo is just dismissing the toast, no rollback API call).
 *
 * @param {Object} opts
 * @param {string} opts.message      - Toast message (e.g. "Task archived")
 * @param {Function} opts.onCommit   - Async function called after timeout. Receives no args.
 * @param {Function} [opts.onUndo]   - Optional callback when user clicks Undo (before commit)
 * @param {number} [opts.timeout]    - Ms before commit (default 5000)
 */
export const withUndo = ({ message, onCommit, onUndo, timeout = 5000 }) => {
    let committed = false;

    const toastId = toast.custom(
        (t) => (
            <div
                style={{
                    background: '#323232',
                    color: '#fff',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    fontSize: '14px',
                    fontWeight: 500,
                    minWidth: '280px'
                }}
            >
                <span style={{ flex: 1 }}>{message}</span>
                <button
                    onClick={() => {
                        if (committed) return;
                        committed = true;
                        toast.dismiss(t.id);
                        if (onUndo) onUndo();
                    }}
                    style={{
                        background: 'transparent',
                        color: '#27ae86',
                        border: 'none',
                        fontWeight: 700,
                        fontSize: '14px',
                        cursor: 'pointer',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        padding: '4px 8px',
                        borderRadius: '4px'
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(39, 174, 134, 0.15)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                    Undo
                </button>
            </div>
        ),
        { duration: timeout, id: `undo-${Date.now()}` }
    );

    // Commit after timeout if not undone
    setTimeout(async () => {
        if (committed) return;
        committed = true;
        toast.dismiss(toastId);
        try {
            await onCommit();
        } catch (err) {
            toast.error('Action failed');
        }
    }, timeout);

    return toastId;
};
